import { useCallback, useState } from "react";
import { persistReturningUser } from "../utils/returningUser.js";
import { t } from "../utils/translate.js";
export function useSignInForm(api, config, initialEmail) {
    const [state, setState] = useState({
        email: initialEmail || config.initialEmail || '',
        password: '',
        errors: {},
        isLoading: false
    });
    const setEmail = useCallback((email)=>{
        // Normalize at the form boundary so the wire payload is deterministic.
        // KC's username lookup is case-insensitive, so login succeeds either
        // way; the lowercase + trim makes the request body stable for caching,
        // logging, and fingerprinting downstream.
        setState((prev)=>({
                ...prev,
                email: email.trim().toLowerCase(),
                errors: {
                    ...prev.errors,
                    email: undefined
                }
            }));
    }, []);
    const setPassword = useCallback((password)=>{
        setState((prev)=>({
                ...prev,
                password,
                errors: {
                    ...prev.errors,
                    password: undefined
                }
            }));
    }, []);
    const submit = useCallback(async ()=>{
        const errors = {};
        if (!state.email) {
            errors.email = t(config, 'auth.error.requiredField');
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(state.email)) {
            errors.email = t(config, 'auth.error.invalidEmail');
        }
        if (!state.password) {
            errors.password = t(config, 'auth.error.requiredField');
        }
        if (errors.email || errors.password) {
            setState((prev)=>({
                    ...prev,
                    errors
                }));
            return;
        }
        setState((prev)=>({
                ...prev,
                isLoading: true,
                errors: {}
            }));
        try {
            // Check SSO enforcement before attempting password login
            const ssoResult = await api.getSsoEnforced(state.email);
            if (ssoResult.enforceSso && ssoResult.alias) {
                const url = new URL(`${config.keycloakUrl}/realms/${config.keycloakRealm}/protocol/openid-connect/auth`);
                url.searchParams.set('client_id', config.keycloakClientId);
                url.searchParams.set('redirect_uri', window.location.origin);
                url.searchParams.set('response_type', 'code');
                url.searchParams.set('scope', 'openid');
                url.searchParams.set('kc_idp_hint', ssoResult.alias);
                window.location.href = url.toString();
                return;
            }
            const { result, error } = await api.login(state.email, state.password);
            if (error) {
                const errorMessage = error.code === 'INVALID_CREDENTIALS' ? t(config, 'auth.error.invalidCredentials') : error.code === 'RATE_LIMITED' ? t(config, 'auth.error.rateLimited') : error.message;
                setState((prev)=>({
                        ...prev,
                        isLoading: false,
                        errors: {
                            form: errorMessage
                        }
                    }));
                config.onLoginError?.(error);
                return;
            }
            if (result?.requiresAction) {
                setState((prev)=>({
                        ...prev,
                        isLoading: false
                    }));
                config.onRequiresAction?.();
                return;
            }
            if (result?.tokens) {
                persistReturningUser(result.tokens);
                setState((prev)=>({
                        ...prev,
                        isLoading: false
                    }));
                config.onLoginSuccess(result.tokens);
            }
        } catch  {
            setState((prev)=>({
                    ...prev,
                    isLoading: false,
                    errors: {
                        form: t(config, 'auth.error.networkError')
                    }
                }));
        }
    }, [
        state.email,
        state.password,
        api,
        config
    ]);
    return {
        email: state.email,
        password: state.password,
        errors: state.errors,
        isLoading: state.isLoading,
        setEmail,
        setPassword,
        submit
    };
}

//# sourceMappingURL=useSignInForm.js.map